import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmployeeCombobox } from "@/components/ui/EmployeeCombobox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FloatField, FloatWrapper } from "@/components/ui/FormField";
import { ArrowLeft, Inbox, Plus, Send, MessageSquare, Search } from "lucide-react";
import {
  OnboardingMessageThread, OnboardingMessage,
  apiGetOnboardingThreads, apiGetOnboardingThread, apiReplyOnboardingThread,
  apiComposeDirectMessage, apiGetEmployees,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { useToast } from "@/hooks/use-toast";
import { ListSkeleton } from "@/components/PageSkeleton";
import { getOnboardingLocale } from "@/lib/onboarding-format";

function formatThreadTime(dateStr: string, t: any, language: string | undefined): string {
  const d = new Date(dateStr);
  const locale = getOnboardingLocale(language);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return t("onboarding.yesterday", "Yesterday");
  if (diffDays < 7) return d.toLocaleDateString(locale, { weekday: "short" });
  return d.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

function formatMsgTime(dateStr: string, language: string | undefined): string {
  return new Date(dateStr).toLocaleTimeString(getOnboardingLocale(language), { hour: "2-digit", minute: "2-digit" });
}

function formatDateDivider(dateStr: string, t: any, language: string | undefined): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return t("onboarding.today", "Today");
  if (diffDays === 1) return t("onboarding.yesterday", "Yesterday");
  return d.toLocaleDateString(getOnboardingLocale(language), { weekday: "long", month: "long", day: "numeric" });
}

export default function OnboardingMessages() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState<OnboardingMessageThread[]>([]);
  const [activeThread, setActiveThread] = useState<OnboardingMessageThread | null>(null);
  const [messages, setMessages] = useState<OnboardingMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  // Compose new thread
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeRecipientId, setComposeRecipientId] = useState("");
  const [composeContent, setComposeContent] = useState("");
  const [composeSending, setComposeSending] = useState(false);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);

  useEffect(() => { loadThreads(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    if (composeOpen && allEmployees.length === 0) {
      apiGetEmployees().then(setAllEmployees).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeOpen]);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const result = await apiGetOnboardingThreads();
      setThreads(result);

      const withUserId = searchParams.get("with");
      if (withUserId) {
        setSearchParams({}, { replace: true });
        const target = result.find((thread) => thread.otherUserId === Number(withUserId));
        if (target) await openThread(target);
      }
    } catch (err: any) {
      console.error("[Messages] loadThreads error:", err);
      toast({
        title: t("onboarding.errorLoadingMessages", "Failed to load messages"),
        description: err?.message || t("onboarding.tryAgainLater", "Please try again later."),
        variant: "destructive",
      });
    } finally { setLoading(false); }
  };

  const handleCompose = async () => {
    if (!composeRecipientId || !composeContent.trim() || composeSending) return;
    const selectedRecipient = allEmployees.find((employee) => String(employee.id) === String(composeRecipientId));
    const recipientEmployeeId = Number(selectedRecipient?.id ?? composeRecipientId);
    if (!selectedRecipient || !Number.isInteger(recipientEmployeeId) || recipientEmployeeId <= 0) {
      toast({
        title: t("onboarding.errorSendingMessage", "Failed to send message"),
        description: t("onboarding.selectValidRecipient", "Please select a valid recipient."),
        variant: "destructive",
      });
      return;
    }
    setComposeSending(true);
    try {
      const { threadId } = await apiComposeDirectMessage(recipientEmployeeId, composeContent.trim()); // recipientEmployeeId is the employee table id
      setComposeOpen(false);
      setComposeContent("");
      setComposeRecipientId("");
      toast({
        title: t("onboarding.messageSent", "Message sent"),
        description: t("onboarding.messageSentDesc", "Your message was delivered successfully."),
      });
      await loadThreads();
      // Open the newly created thread
      const fresh = await apiGetOnboardingThreads();
      const newThread = fresh.find((tt) => tt.threadId === threadId);
      if (newThread) {
        setActiveThread(newThread);
        const msgs = await apiGetOnboardingThread(threadId);
        setMessages(msgs);
      }
    } catch (err: any) {
      console.error("[Messages] handleCompose error:", err);
      toast({
        title: t("onboarding.errorSendingMessage", "Failed to send message"),
        description: err?.message || t("onboarding.tryAgainLater", "Please try again later."),
        variant: "destructive",
      });
    } finally { setComposeSending(false); }
  };

  const openThread = async (thread: OnboardingMessageThread) => {
    setActiveThread(thread);
    try {
      const msgs = await apiGetOnboardingThread(thread.threadId);
      setMessages(msgs);
      setThreads((prev) => prev.map((t) => t.threadId === thread.threadId ? { ...t, unreadCount: 0 } : t));
    } catch (err: any) {
      console.error("[Messages] openThread error:", err);
      toast({
        title: t("onboarding.errorLoadingThread", "Failed to load conversation"),
        description: err?.message || t("onboarding.tryAgainLater", "Please try again later."),
        variant: "destructive",
      });
    }
  };

  const handleSend = async () => {
    if (!activeThread || !input.trim() || sending) return;
    const content = input.trim();
    setInput("");
    setSending(true);
    try {
      const msg = await apiReplyOnboardingThread(activeThread.threadId, content);
      setMessages((prev) => [...prev, msg]);
    } catch (err: any) {
      console.error("[Messages] handleSend error:", err);
      // Restore the input so the user can retry without losing their message
      setInput(content);
      toast({
        title: t("onboarding.errorSendingMessage", "Failed to send message"),
        description: err?.message || t("onboarding.tryAgainLater", "Please try again later."),
        variant: "destructive",
      });
    } finally { setSending(false); }
  };

  const filteredThreads = threads.filter((t) =>
    t.otherName?.toLowerCase().includes(search.toLowerCase())
  );

  // Group messages by date for dividers
  const msgWithDividers: Array<{ type: "divider"; label: string } | { type: "msg"; msg: OnboardingMessage; showAvatar: boolean }> = [];
  let lastDate = "";
  let lastSenderId: number | null = null;
  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const dateKey = new Date(msg.createdAt).toDateString();
    if (dateKey !== lastDate) {
      msgWithDividers.push({ type: "divider", label: formatDateDivider(msg.createdAt, t, i18n.resolvedLanguage || i18n.language) });
      lastDate = dateKey;
      lastSenderId = null;
    }
    const nextMsg = messages[i + 1];
    const isLast = !nextMsg || nextMsg.senderId !== msg.senderId || new Date(nextMsg.createdAt).toDateString() !== dateKey;
    msgWithDividers.push({ type: "msg", msg, showAvatar: isLast });
    lastSenderId = msg.senderId;
  }

  const myId = Number(user?.id);

  return (
    <AppLayout>
      <div className="flex h-[calc(100vh-5rem)] overflow-hidden rounded-xl border border-border shadow-sm bg-background">
        {/* ── Sidebar ── */}
        <div className={`${activeThread ? "hidden md:flex" : "flex"} flex-col w-full md:w-72 shrink-0 border-r border-border`}>
          {/* Header */}
          <div className="px-4 pt-4 pb-3 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                {t("onboarding.messages", "Messages")}
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 text-xs"
                onClick={() => setComposeOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" />
                {t("onboarding.newMessage", "New Message")}
              </Button>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={t("onboarding.searchConversations", "Search conversations...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm bg-secondary border-0 focus-visible:ring-1"
              />
            </div>
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-3">
                <ListSkeleton rows={6} />
              </div>
            ) : filteredThreads.length === 0 ? (
              <div className="text-center py-12">
                <Inbox className="h-10 w-10 mx-auto text-muted-foreground/30 mb-2" />
                <p className="text-sm text-muted-foreground">{t("onboarding.noConversations", "No conversations")}</p>
              </div>
            ) : (
              filteredThreads.map((thread) => {
                const isActive = activeThread?.threadId === thread.threadId;
                const hasUnread = thread.unreadCount > 0;
                return (
                  <button
                    key={thread.threadId}
                    onClick={() => openThread(thread)}
                    className={`w-full px-3 py-3 flex items-center gap-3 hover:bg-secondary/60 transition-colors relative
                      ${isActive ? "bg-primary/10 border-r-2 border-primary" : ""}`}
                  >
                    <div className="relative shrink-0">
                      <EmployeeAvatar
                        avatarUrl={thread.otherAvatarUrl}
                        avatarColor="from-indigo-400 to-purple-500"
                        initials={thread.otherName?.charAt(0) || "?"}
                        size="md"
                      />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className={`text-sm truncate ${hasUnread ? "font-bold text-foreground" : "font-medium text-foreground/80"}`}>
                          {thread.otherName}
                        </p>
                        <span className={`text-[11px] shrink-0 ml-2 ${hasUnread ? "text-primary font-semibold" : "text-muted-foreground"}`}>
                          {formatThreadTime(thread.lastAt, t, i18n.resolvedLanguage || i18n.language)}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${hasUnread ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                        {thread.lastMessage}
                      </p>
                    </div>
                    {hasUnread && (
                      <span className="shrink-0 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
                        {thread.unreadCount}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Chat area ── */}
        <div className={`${activeThread ? "flex" : "hidden md:flex"} flex-col flex-1 min-w-0`}>
          {activeThread ? (
            <>
              {/* Chat header */}
              <div className="px-4 py-3 border-b border-border flex items-center gap-3 bg-background">
                <Button variant="ghost" size="icon" className="h-8 w-8 md:hidden shrink-0" onClick={() => setActiveThread(null)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="relative shrink-0">
                  <EmployeeAvatar
                    avatarUrl={activeThread.otherAvatarUrl}
                    avatarColor="from-indigo-400 to-purple-500"
                    initials={activeThread.otherName?.charAt(0) || "?"}
                    size="sm"
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-tight">{activeThread.otherName}</p>
                  <p className="text-xs text-muted-foreground">{t("onboarding.onboardingConversation", "Onboarding conversation")}</p>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-0.5">
                {msgWithDividers.map((item, i) => {
                  if (item.type === "divider") {
                    return (
                      <div key={`divider-${i}`} className="flex items-center gap-3 py-4">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-[11px] text-muted-foreground font-medium">{item.label}</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                    );
                  }
                  const { msg, showAvatar } = item;
                  const isMine = msg.senderId === myId;

                  return (
                    <div key={msg.id} className={`flex items-end gap-2 ${isMine ? "flex-row-reverse" : "flex-row"} ${showAvatar ? "mb-1" : "mb-0"}`}>
                      {/* Avatar placeholder to keep alignment */}
                      <div className="w-7 shrink-0">
                        {!isMine && showAvatar && (
                          <EmployeeAvatar
                            avatarUrl={activeThread.otherAvatarUrl}
                            avatarColor="from-indigo-400 to-purple-500"
                            initials={activeThread.otherName?.charAt(0) || "?"}
                            size="xs"
                          />
                        )}
                      </div>

                      <div className={`flex flex-col max-w-[65%] ${isMine ? "items-end" : "items-start"}`}>
                        <div className={`px-3.5 py-2 text-sm whitespace-pre-wrap break-words leading-relaxed
                          ${isMine
                            ? "bg-primary text-primary-foreground rounded-2xl rounded-br-sm"
                            : "bg-secondary text-foreground rounded-2xl rounded-bl-sm"
                          }`}
                        >
                          {msg.content}
                        </div>
                        {showAvatar && (
                          <p className={`text-[10px] text-muted-foreground mt-1 ${isMine ? "mr-1" : "ml-1"}`}>
                            {formatMsgTime(msg.createdAt, i18n.resolvedLanguage || i18n.language)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              {/* Input */}
              <div className="border-t border-border px-4 py-3 bg-background">
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex items-center gap-2">
                  <Input
                    placeholder="Aa"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    disabled={sending}
                    className="flex-1 rounded-full bg-secondary border-0 focus-visible:ring-1 text-sm"
                  />
                  <Button
                    type="submit"
                    size="icon"
                    disabled={!input.trim() || sending}
                    className="rounded-full h-9 w-9 shrink-0"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="h-16 w-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-muted-foreground/40" />
                </div>
                <p className="font-semibold text-foreground mb-1">{t("onboarding.yourMessages", "Your Messages")}</p>
                <p className="text-sm text-muted-foreground">{t("onboarding.selectConversationChat", "Select a conversation to start chatting")}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compose new message dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-[460px] p-0 gap-0 overflow-hidden">
          <div className="px-6 pt-6 pb-4 bg-muted/30 border-b">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2.5 text-base font-semibold">
                <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Plus className="h-4 w-4 text-primary" />
                </div>
                {t("onboarding.newMessage", "New Message")}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-4 space-y-4">
            <FloatWrapper label={t("onboarding.sendTo", "Send to")} required>
              <EmployeeCombobox
                employees={allEmployees}
                value={composeRecipientId}
                onChange={setComposeRecipientId}
                placeholder={t("onboarding.selectEmployee", "Search employee...")}
                excludeIds={user?.employeeId ? [user.employeeId] : []}
              />
            </FloatWrapper>
            <FloatField
              label={t("onboarding.messageLabel", "Message")}
              type="textarea"
              required
              placeholder={t("onboarding.typeYourMessage", "Type your message...")}
              value={composeContent}
              onChange={(v) => setComposeContent(v as string)}
            />
            <p className="text-[11px] text-muted-foreground -mt-2">{t("onboarding.ctrlEnterSend", "Ctrl+Enter to send")}</p>
          </div>
          <div className="px-6 pb-6 pt-4 border-t bg-muted/20 flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setComposeOpen(false); setComposeRecipientId(""); setComposeContent(""); }} disabled={composeSending}>
              {t("onboarding.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleCompose}
              disabled={!composeRecipientId || !composeContent.trim() || composeSending}
              className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 border-0"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {composeSending ? t("onboarding.sending", "Sending…") : t("onboarding.sendMessage", "Send Message")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
